import {
  customElement,
  DeesElement,
  domtools,
  type TemplateResult,
  html,
  property,
  type CSSResult,
  state,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-windowcontrols': DeesWindowControls;
  }
}

@customElement('dees-windowcontrols')
export class DeesWindowControls extends DeesElement {
  // STATIC
  public static demo = () => html`<dees-windowcontrols></dees-windowcontrols>`;
  public static demoGroups = ['Utility'];

  // Instance
  @property({
    reflect: true,
  })
  accessor type: 'mac' | 'linux' | 'windows' = 'mac';

  @property({
    reflect: true,
  })
  accessor position: 'left' | 'right' = 'left';

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        --dees-window-control-size: var(--dees-spacing-md);
        --dees-window-control-gap: var(--dees-spacing-sm);
        --dees-window-control-close: var(--dees-color-accent-error);
        --dees-window-control-dock: var(--dees-color-accent-warning);
        --dees-window-control-minmax: var(--dees-color-accent-success);
        --dees-window-control-edge: var(--dees-color-border-subtle);
        --dees-window-control-highlight: var(--dees-color-fill-primary);

        position: relative;
        display: block;
        box-sizing: border-box;
        height: 100%;
        padding-inline: var(--dees-spacing-lg) var(--dees-spacing-md);
        -webkit-app-region: no-drag;
      }

      .windowControls {
        height: 100%;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: var(--dees-window-control-gap);
      }

      .windowControls div {
        position: relative;
        flex: 0 0 var(--dees-window-control-size);
        width: var(--dees-window-control-size);
        height: var(--dees-window-control-size);
        display: block;
        border-radius: var(--dees-radius-full);
        margin: 0;
        padding: 0;
        border: 1px solid var(--dees-window-control-edge);
        background: var(--dees-color-text-secondary);
        box-shadow:
          inset 0 0 0 1px var(--dees-window-control-highlight),
          var(--dees-shadow-xs);
        transition:
          filter var(--dees-transition-fast) var(--dees-ease-standard),
          transform var(--dees-transition-fast) var(--dees-ease-standard),
          box-shadow var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .windowControls div.close {
        background: var(--dees-window-control-close);
      }

      .windowControls div.toDock {
        background: var(--dees-window-control-dock);
      }

      .windowControls div.minMax {
        background: var(--dees-window-control-minmax);
      }

      .windowControls:hover div {
        filter: saturate(1.08) brightness(1.02);
      }

      .windowControls div:hover {
        transform: scale(1.08);
        box-shadow:
          inset 0 0 0 1px var(--dees-window-control-highlight),
          var(--dees-shadow-sm);
      }

      .windowControls div:active {
        filter: brightness(0.92);
        transform: scale(0.94);
      }

      @media (prefers-reduced-motion: reduce) {
        .windowControls div {
          transition: none;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      ${(this.type === 'mac' && this.position === 'left') ||
      ((this.type === 'linux' || this.type === 'windows') && this.position === 'right')
        ? html`
            <div class="windowControls">
              <div class="close"></div>
              <div class="toDock"></div>
              <div class="minMax"></div>
            </div>
          `
        : html``}
    `;
  }
}
